import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import aboutImage from "@/assets/hero-archivist.jpg?format=webp&quality=72";

const ABOUT_SHARE_IMAGE = `https://houseofapocrypha.com${aboutImage}`;

export const Route = createFileRoute("/about")({
  head: () => {
    const title = "About Us — Natural Perfumery & Contact · House of Apocrypha";
    const description =
      "Natural perfumery from raw tinctures, distilled oils and absolutes. Read our philosophy and reach the studio.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://houseofapocrypha.com/about" },
        { property: "og:image", content: ABOUT_SHARE_IMAGE },
        { property: "og:image:alt", content: "Inside the House of Apocrypha studio." },
        { name: "twitter:image", content: ABOUT_SHARE_IMAGE },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: "https://houseofapocrypha.com/about" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: title,
            description,
            url: "https://houseofapocrypha.com/about",
            isPartOf: { "@type": "WebSite", name: "House of Apocrypha", url: "https://houseofapocrypha.com/" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "About", item: "https://houseofapocrypha.com/about" },
            ],
          }),
        },
      ],
    };
  },
  component: AboutPage,
});

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
};

const materials = [
  {
    title: "Raw Natural Materials",
    sub: "Macerations, Concretes, Tinctures",
    body: "Whole plant extracts with minimal processing. These dense, waxy, or resinous materials carry the full complexity of the original plant.",
  },
  {
    title: "Condensed Natural Extracts",
    sub: "Essential Oils, Absolutes, CO₂ Extracts",
    body: "Highly concentrated, these are the heart of botanical perfumery — distilled or extracted directly from flowers, roots, leaves, bark, or resins.",
  },
  {
    title: "Isolated Natural Molecules",
    sub: "Refined plant compounds",
    body: "Individual scent compounds separated from essential oils — like vanillin from clove or citral from lemongrass. Still natural in origin, but more refined.",
  },
];

const excluded = [
  {
    title: "Synthetic Aroma Molecules",
    body: "Lab-created compounds that do not exist in nature, commonly used to mimic natural scents. We find they lack the soul, texture, and vitality of real botanical materials — and many trigger headaches or discomfort for sensitive individuals, including our founder.",
  },
  {
    title: "Nature-Identical Compounds",
    body: "Though chemically identical to naturally derived molecules, these are made in laboratories, often from petroleum byproducts. They remove the material from its context — the plant, the soil, the light it grew in. For us, that context is part of the magic.",
  },
  {
    title: "Fragrance Oils",
    body: "Umbrella terms that can hide dozens of undisclosed ingredients. We don't use them. Our perfumes are made entirely from plant extractions and plant-derived materials.",
  },
  {
    title: "Preservatives & Stabilizers",
    body: "Our perfumes are alcohol-based and self-preserving. We add no preservatives, phthalates, UV inhibitors, or other stabilizing additives common in mainstream formulations.",
  },
];

const whyParagraphs = [
  "When I first began experimenting with scent, I worked with both synthetics and natural materials. I was fascinated by the clarity and impact that certain synthetic aroma molecules could bring — bold, transformative, long-lasting. But over time I noticed something else: when I spent long hours working with synthetics, I didn't feel well. Subtle at first — light headaches, a hint of nausea — but persistent. The same happened when I wore many commercial perfumes. It felt like something in my body recoiled.",
  "Working with naturals felt entirely different. There was — and still is — an aura around them. Not metaphorical, but sensory: herbal, dimensional, alive. Even when raw or unruly, they hold a kind of wisdom encoded in their complexity. Each material carries the memory of sun, soil, root, and time. They don't shout. They evolve. They invite.",
  "This is why House of Apocrypha is dedicated to natural perfumery. Not because we believe synthetics are \u201Cbad\u201D — we don't — but because naturals offer something more aligned with our vision: scent as ritual, as memory, as a return.",
  "So I transitioned. Slowly at first, then completely. Now I work only with materials derived directly from the natural world — raw tinctures, distilled oils, delicate absolutes. The difference isn't just olfactory; it's energetic. When I blend now, it feels like I'm part of something older, as though the process itself is a ritual.",
];

function AboutPage() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrorMsg(null);

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") ?? "").trim().slice(0, 100),
      email: String(data.get("email") ?? "").trim().slice(0, 255),
      phone: String(data.get("phone") ?? "").trim().slice(0, 40),
      comment: String(data.get("comment") ?? "").trim().slice(0, 5000),
      _subject: "New contact form message — House of Apocrypha",
    };

    if (!payload.name || !payload.email || !payload.comment) {
      setErrorMsg("Please fill in name, email and a message.");
      setSubmitting(false);
      return;
    }

    try {
      const res = await fetch("https://formspree.io/f/mykqbply", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Formspree responded ${res.status}`);
      setSent(true);
      form.reset();
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong sending your message. Please try again, or email hello@houseofapocrypha.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <main>
        <nav className="mx-auto max-w-4xl px-6 pt-8 text-sm">
          <span className="text-muted-foreground">House</span>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">About Us</span>
        </nav>

        {/* Hero */}
        <section className="mx-auto max-w-3xl px-6 py-16 text-center lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="eyebrow">Our Story</p>
            <h1 className="mt-5 text-4xl text-parchment sm:text-5xl lg:text-6xl">
              Return to <span className="text-gradient-gold">Naturals</span>
            </h1>
            <p className="mx-auto mt-7 max-w-xl font-serif text-2xl italic leading-relaxed text-gold/90">
              Natural perfumes rooted in story, ritual &amp; alchemy. Made slowly, in small
              batches, by hand.
            </p>
          </motion.div>
          <div className="ornament mt-12">
            <span>✶</span>
          </div>
        </section>

        {/* Why we chose naturals — founder narrative comes first */}
        <section className="mx-auto max-w-3xl px-6 py-12">
          <motion.div {...fade} className="text-center">
            <p className="eyebrow">A Note from the Founder</p>
            <h2 className="mt-5 text-3xl text-parchment sm:text-4xl">Why We Chose Naturals</h2>
          </motion.div>
          <motion.div
            {...fade}
            className="mt-10 space-y-6 text-xl leading-relaxed text-foreground/85"
          >
            {whyParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <p className="font-serif text-2xl italic text-gold/90">
              Our perfumes aren't built to dominate a room. They are tools for return — for
              grounding, memory, reverie — a daily ritual that helps us pause, reconnect, and
              remember that we are part of the natural world, not separate from it.
            </p>
            <p>
              This is also why I release in chapters rather than keeping a
              constant shop. A formula made in a particular season is a record
              of that season — the batch of labdanum, the harvest of flowers,
              the particular amber of that year's work. To produce without pause
              would be to erase that record. A chapter opened, closed, and
              entered into the archive is the only honest way I know to work
              with materials that are, themselves, alive to time.
            </p>
          </motion.div>
        </section>

        <div className="ornament my-8">
          <span>✶</span>
        </div>

        {/* What we mean by natural */}
        <section className="mx-auto max-w-5xl px-6 py-12">
          <motion.div {...fade} className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl text-parchment sm:text-4xl">What We Mean by Natural</h2>
            <p className="mt-6 text-lg leading-relaxed text-foreground/80">
              At House of Apocrypha, we work exclusively with materials derived directly from
              nature — minimally altered, richly complex, and rooted in tradition. Here's how we
              define the materials we choose to work with.
            </p>
          </motion.div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {materials.map((m, i) => (
              <motion.article
                key={m.title}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.1 }}
                className="rounded-md border border-border bg-card/40 p-7"
              >
                <span className="font-display text-3xl text-gold/40">{`0${i + 1}`}</span>
                <h3 className="mt-4 font-display text-lg uppercase tracking-[0.14em] text-parchment">
                  {m.title}
                </h3>
                <p className="mt-1 font-serif text-base italic text-gold/80">{m.sub}</p>
                <p className="mt-4 text-base leading-relaxed text-foreground/75">{m.body}</p>
              </motion.article>
            ))}
          </div>
        </section>

        {/* What we don't use */}
        <section className="mx-auto max-w-5xl px-6 py-12">
          <motion.div {...fade} className="mx-auto max-w-2xl text-center">
            <p className="eyebrow">What We Leave Out</p>
            <h2 className="mt-5 text-3xl text-parchment sm:text-4xl">We do not use</h2>
          </motion.div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {excluded.map((e, i) => (
              <motion.article
                key={e.title}
                {...fade}
                transition={{ ...fade.transition, delay: (i % 2) * 0.1 }}
                className="flex gap-4 rounded-md border border-border bg-card/30 p-6"
              >
                <span aria-hidden className="mt-0.5 font-display text-xl text-gold/70">
                  ✕
                </span>
                <div>
                  <h3 className="font-display text-base uppercase tracking-[0.14em] text-parchment">
                    {e.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-foreground/75">{e.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </section>


        {/* Contact */}
        <section id="contact" className="border-t border-border bg-card/20 py-20">
          <div className="mx-auto max-w-2xl px-6">
            <motion.div {...fade} className="text-center">
              <p className="eyebrow">Reach the Studio</p>
              <h2 className="mt-5 text-3xl text-parchment sm:text-4xl">Contact</h2>
              <p className="mx-auto mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
                Send us your inquiry below. We are a small studio — all messages are read by hand,
                and replies may take a day or two.
              </p>
            </motion.div>

            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-12 rounded-md border border-gold/30 bg-card/50 p-10 text-center"
              >
                <p className="font-serif text-2xl italic text-gold">Thank you.</p>
                <p className="mt-3 text-lg text-foreground/80">
                  Your message has been received. We'll be in touch soon.
                </p>
              </motion.div>
            ) : (
              <motion.form
                {...fade}
                onSubmit={handleContactSubmit}
                className="mt-12 space-y-6"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Name" name="name" required />
                  <Field label="Email" name="email" type="email" required />
                </div>
                <Field label="Phone number" name="phone" type="tel" />
                <div>
                  <label
                    htmlFor="comment"
                    className="font-display text-xs uppercase tracking-[0.2em] text-gold"
                  >
                    Comment
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    rows={5}
                    required
                    maxLength={5000}
                    className="mt-2 w-full rounded-sm border border-border bg-background/60 px-4 py-3 font-serif text-lg text-parchment outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-gold"
                  />
                </div>
                {errorMsg && (
                  <p className="text-sm text-destructive" role="alert">
                    {errorMsg}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-sm bg-gradient-to-r from-gold-soft to-gold px-8 py-4 font-display text-xs uppercase tracking-[0.24em] text-ink transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </motion.form>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required = false,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="font-display text-xs uppercase tracking-[0.2em] text-gold">
        {label}
        {required && <span className="text-gold/60"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        className="mt-2 w-full rounded-sm border border-border bg-background/60 px-4 py-3 font-serif text-lg text-parchment outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-gold"
      />
    </div>
  );
}
